const RECORDING_VERSION = 1;
const DEFAULT_STEP_SECONDS = 1 / 60;
const DEFAULT_MAX_FRAMES = 216000;

const clamp = (value, min = -1, max = 1) => Math.min(max, Math.max(min, value));
const finiteOr = (value, fallback = 0) => Number.isFinite(value) ? value : fallback;
const clone = (value) => JSON.parse(JSON.stringify(value));

function normalizeActionState(state = {}) {
  return {
    down: Boolean(state.down),
    pressed: Boolean(state.pressed),
    released: Boolean(state.released),
  };
}

function normalizeSnapshot(snapshot = {}) {
  return {
    version: Number.isInteger(snapshot.version) ? snapshot.version : 1,
    sequence: Number.isInteger(snapshot.sequence) ? snapshot.sequence : 0,
    timestamp: finiteOr(snapshot.timestamp),
    move: {
      x: clamp(finiteOr(snapshot.move?.x)),
      y: clamp(finiteOr(snapshot.move?.y)),
    },
    look: {
      x: clamp(finiteOr(snapshot.look?.x)),
      y: clamp(finiteOr(snapshot.look?.y)),
    },
    zoom: clamp(finiteOr(snapshot.zoom)),
    actions: Object.fromEntries(
      Object.entries(snapshot.actions || {}).map(([name, state]) => [name, normalizeActionState(state)]),
    ),
    sources: clone(snapshot.sources || {}),
  };
}

function validateStepSeconds(stepSeconds) {
  if (!Number.isFinite(stepSeconds) || stepSeconds <= 0) {
    throw new RangeError('stepSeconds must be a positive finite number');
  }
  return stepSeconds;
}

function parseRecording(recording) {
  const parsed = typeof recording === 'string' ? JSON.parse(recording) : recording;
  if (!parsed || typeof parsed !== 'object') throw new TypeError('recording must be an object or JSON string');
  if (parsed.version !== RECORDING_VERSION) throw new RangeError(`Unsupported recording version: ${parsed.version}`);
  const stepSeconds = validateStepSeconds(parsed.stepSeconds);
  if (!Array.isArray(parsed.frames)) throw new TypeError('recording.frames must be an array');

  const frames = parsed.frames.map((entry, index) => {
    if (!entry || typeof entry !== 'object') throw new TypeError(`recording frame ${index} is invalid`);
    const frame = Number.isInteger(entry.frame) ? entry.frame : index;
    if (frame !== index) throw new RangeError(`recording frame ${index} must have frame index ${index}`);
    return { frame, input: normalizeSnapshot(entry.input) };
  });

  return {
    version: RECORDING_VERSION,
    inputVersion: Number.isInteger(parsed.inputVersion) ? parsed.inputVersion : 1,
    stepSeconds,
    metadata: clone(parsed.metadata || {}),
    frames,
  };
}

export class InputReplaySystem {
  constructor({ maxFrames = DEFAULT_MAX_FRAMES } = {}) {
    if (!Number.isInteger(maxFrames) || maxFrames <= 0) {
      throw new RangeError('maxFrames must be a positive integer');
    }
    this.maxFrames = maxFrames;
    this.mode = 'idle';
    this.paused = false;
    this.cursor = 0;
    this.activeRecording = null;
    this.loadedRecording = null;
    this.lastReplayFrame = null;
  }

  startRecording({ stepSeconds = DEFAULT_STEP_SECONDS, metadata = {} } = {}) {
    this.activeRecording = {
      version: RECORDING_VERSION,
      inputVersion: 1,
      stepSeconds: validateStepSeconds(stepSeconds),
      metadata: clone(metadata),
      frames: [],
    };
    this.mode = 'recording';
    this.paused = false;
    this.cursor = 0;
    this.lastReplayFrame = null;
    return this.getStatus();
  }

  capture(snapshot) {
    if (this.mode !== 'recording' || !this.activeRecording) return false;
    if (this.activeRecording.frames.length >= this.maxFrames) {
      throw new RangeError(`Input recording exceeded maxFrames (${this.maxFrames})`);
    }
    const frame = this.activeRecording.frames.length;
    this.activeRecording.frames.push({ frame, input: normalizeSnapshot(snapshot) });
    return true;
  }

  stopRecording() {
    if (this.mode !== 'recording' || !this.activeRecording) return null;
    const recording = clone(this.activeRecording);
    this.loadedRecording = recording;
    this.activeRecording = null;
    this.mode = 'idle';
    this.cursor = 0;
    return recording;
  }

  getRecording() {
    const recording = this.activeRecording || this.loadedRecording;
    return recording ? clone(recording) : null;
  }

  exportRecording() {
    const recording = this.getRecording();
    if (!recording) throw new Error('No input recording is available');
    return JSON.stringify(recording);
  }

  importRecording(recording) {
    this.loadedRecording = parseRecording(recording);
    this.activeRecording = null;
    this.mode = 'idle';
    this.paused = false;
    this.cursor = 0;
    this.lastReplayFrame = null;
    return this.getStatus();
  }

  startReplay(recording = undefined) {
    if (recording !== undefined) this.importRecording(recording);
    if (!this.loadedRecording) throw new Error('No input recording is loaded');
    this.mode = 'replaying';
    this.paused = false;
    this.cursor = 0;
    this.lastReplayFrame = null;
    return this.getStatus();
  }

  nextFrame() {
    if (this.mode !== 'replaying' || !this.loadedRecording) return null;
    if (this.paused) return this.lastReplayFrame ? clone(this.lastReplayFrame) : null;
    if (this.cursor >= this.loadedRecording.frames.length) {
      this.mode = 'idle';
      this.lastReplayFrame = null;
      return null;
    }

    const entry = this.loadedRecording.frames[this.cursor];
    const input = normalizeSnapshot(entry.input);
    input.sequence = this.cursor + 1;
    input.timestamp = this.cursor * this.loadedRecording.stepSeconds * 1000;
    input.sources = { ...input.sources, replay: true };
    this.cursor += 1;
    this.lastReplayFrame = input;
    return clone(input);
  }

  pauseReplay() {
    if (this.mode !== 'replaying') throw new Error('Input replay is not active');
    this.paused = true;
    return this.getStatus();
  }

  resumeReplay() {
    if (this.mode !== 'replaying') throw new Error('Input replay is not active');
    this.paused = false;
    return this.getStatus();
  }

  seek(frame) {
    if (!this.loadedRecording) throw new Error('No input recording is loaded');
    if (!Number.isInteger(frame) || frame < 0 || frame > this.loadedRecording.frames.length) {
      throw new RangeError(`frame must be between 0 and ${this.loadedRecording.frames.length}`);
    }
    this.cursor = frame;
    this.lastReplayFrame = frame > 0
      ? normalizeSnapshot(this.loadedRecording.frames[frame - 1].input)
      : null;
    return this.getStatus();
  }

  resetReplay() {
    if (!this.loadedRecording) throw new Error('No input recording is loaded');
    this.cursor = 0;
    this.lastReplayFrame = null;
    return this.getStatus();
  }

  stopReplay() {
    this.mode = 'idle';
    this.paused = false;
    this.cursor = 0;
    this.lastReplayFrame = null;
    return this.getStatus();
  }

  reset() {
    this.mode = 'idle';
    this.paused = false;
    this.cursor = 0;
    this.activeRecording = null;
    this.loadedRecording = null;
    this.lastReplayFrame = null;
    return this.getStatus();
  }

  getStatus() {
    const recording = this.activeRecording || this.loadedRecording;
    const totalFrames = recording?.frames.length || 0;
    return {
      version: RECORDING_VERSION,
      mode: this.mode,
      recording: this.mode === 'recording',
      replaying: this.mode === 'replaying',
      paused: this.paused,
      cursor: this.cursor,
      totalFrames,
      completed: totalFrames > 0 && this.cursor >= totalFrames,
      hasRecording: Boolean(recording),
      stepSeconds: recording?.stepSeconds || null,
    };
  }
}
